/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */

import {AccountingPlan} from '../shared/accounting/accounting.model';
import {Link} from '../shared/api';
import {ContractStatus, SdcSyncStatus} from '../shared/employees/contract/contract.interfaces';
import {EmployeeStatus} from '../shared/employees/employees.model';
import {GroupZoneDescriptor, ZoneValues} from '../shared/form-generator/form-generator.interfaces';
import {OnboardingStatus} from '@gp/onboarding/onboarding.model';

/**
 * Attributes for a cliUser
 */
export interface CliUserAttributes {
  id?: number;
  cliEmpId?: number;
  login: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phoneNumber: string;
  lastConnection: string;
  locale: string;
  notes: string;
  isAdmin: boolean;
  ssoType: SsoTypeEnum;
  page?: number;
  cliCoreId?: number;
  active: boolean;
  preferences?: CliUserPreferences;
}


export interface CliUserDTO {
  /** @format date */
  accessEndDate?: string;
  accessOwnContracts?: boolean;
  active?: boolean;
  /** @format int64 */
  cliCoreId?: number;
  /** @format int64 */
  cliEmpId?: number;
  dateFormat?: string;
  decimalMask?: string;
  email?: string;
  firstName?: string;
  /** @format int64 */
  id?: number;
  integerMask?: string;
  lastName?: string;
  locale?: string;
  login?: string;
  password?: string;
  phoneNumber?: string;
  profileName?: string;
  sendEmail?: boolean;
  timezone?: string;
  title?: string;
}


export interface EmpCliUserDTO {
  /** @format int64 */
  cliCoreId?: number;
  /** @format int64 */
  cliEmpId?: number;
  employeeNumber?: string;
  firstName?: string;
  /** @format int64 */
  id?: number;
  lastName?: string;
}

export enum SsoTypeEnum {
  NONE = 'NONE',
  GP_SSO = 'GP_SSO',
  IPS_SSO = 'IPS_SSO'
}

export enum FeatureEnum {
  EXPENSES = 'EXPENSES',
  E_SIGNATURE = 'E_SIGNATURE',
  HR_CALENDAR = 'HR_CALENDAR'
}

/**
 * Attributes for a cliCore
 */
export interface CliCoreAttributes {
  begindate: string;
  cliRegIdFori18n: number;
  currency: string;
  enddate: string;
  language: string;
  logo: number;
  name: string;
  updatedate: string;
  urlName: string;
}

/**
 * Attributes for a cliEmp
 */
export interface CliEmpAttributes {
  firstName: string;
  lastName: string;
  intName: string;
  intFirstName: string;
  intLastName: string;
  otherid1?: string;
  otherid2?: string;
  otherid3?: string;
  specimen?: boolean;
  actif?: boolean;
  id?: number;
  cliEmpId?: number;
  maidenName?: string;
  nationalId?: string;
  birthDate?: string;
  lastUpdateDate: string;
  cliCoreID: number;
  contract: {
    contract: any;
    contractDatas: any;
  };
  empContracts: any;
  userActive: boolean;
  userEmail: string;
  userLogin: string;
  email?: string;
  status?: EmployeeStatus;
  onboardingStatus?: OnboardingStatus;
}

export interface CliEmpUser {
  id: number;
  cliEmpId: number;
  employeeNumber: number;
  cliCoreId: number;
  firstName: string;
  lastName: string;
}

/**
 * Attributes for a cliContract
 */
export interface EmpContractAttributes {
  id?: number;
  payroll?: boolean;
  currentPayPeriodContractId?: number;
  cliCoreID?: number;
  begindate: string;
  enddate: string;
  mainContract: boolean;
  entityTreeWithPaySerieid: number;
  entityWithPaySerieid: number;
  cliEmp: number;
  cliRegID: number;
  cliRegNickName: string;
  links: any[];
  haveFlag?: boolean;
  flag?: { href: string };
  flagUri?: string;
  empBankAccounts?: any;
  readOnly: boolean;
  canBeDeleted?: boolean;
  status?: ContractStatus;
  email: string;
  personMasterDataSyncStatus?: SdcSyncStatus;
  payrollInputSyncStatus?: SdcSyncStatus;
}

/**
 * Attributes for cliProfile
 */
export interface CliProfileAttributes {
  roles: string;
  transid: string;
}

/**
 * CliReg : a client regulation definition
 */
export interface CliReg {
  cliCoreID: number;
  id: number;
  nickName: string;
  parentreg: number;
  parentRegIdVisible: number;
  read: boolean;
  userSelectable: boolean;
  userVisible: boolean;
  write: boolean;
  gloReg?: number;
}

/**
 * EmpVariable : an employee variable element
 */
export interface EmpVariableAttributes {
  id?: number;
  begindate: string;
  isBeginDateLinkedToContract: boolean;
  enddate: string;
  isEndDateLinkedToContract: boolean;
  name: string;
  value: number;
  empContract: number;
  evtype: string;
}

export interface EmpAbsenceAttributes {
  id?: number;
  begin: string;
  end: string;
  type: string;
  beginDayFactor: number;
  endDayFactor: number;
  empContract: number;
}

export interface PaySerie {
  id: string;
  cliCoreId: number;
  type: string;
  cliRegIds: number[];
  currency: string;
  duration: string;
  oldestPayPeriodBeginDate: string;
  transId: string;
  links: Link[];
  accountingPlan: AccountingPlan;
}

export interface PayPeriodAttributes {
  begindate: string;
  closingdate: string;
  progress: number;
  enddate: string;
  norecallbefore: boolean;
  payrolltype: string;
  serie: PaySerie;
  status: string;
}

export interface PayPeriod {
  beginDate: string;
  closingDate: string;
  paymentDate: string;
  progress: number;
  endDate: string;
  noRecallBefore: boolean;
  payrollType: string;
  paySerie: PaySerie;
  status: string;
}

/**
 * Time and Attendance : an employee absence element
 */
export interface EmployeeAbsence {
  id?: number;
  date: string;
  type: string;
  paidfactor: number;
  workedfactor: number;
  empContract: number;
}

export interface EmpCertificate {
  attributes?: any;
  id?: number;
  payroll?: boolean;
  date?: Date;
  step?: number;
  effectiveDate?: string;
  isCommunication: boolean;
  isAskZone: boolean;
  descriptionTransId?: string;
  arguments: Array<ReportArguments>;
  reportType: string;
}

export interface CreateUserData {
  cliEmpId?: number;
  login: string;
  firstName: string;
  lastName: string;
  email: string;
  id?: number;
  title?: string;
  phoneNumber?: string;
  language?: string;
  dateFormat?: string;
  decimalMask?: string;
  integerMask?: string;
  timezone?: string;
  password?: string;
  active?: boolean;
  profileName?: string;
}

export interface ReportDefinition {
  id: number | string;
  cliRegId: number;
  descriptionTransId: string;
  reportName: string;
  reportPeriodEditable?: boolean;
  arguments?: Array<ReportArguments>;
  reportType: string;
  templateName: string;
  gloRegTransId?: string;
  gloRegId?: number;
  contentType?: string;
  family?: string;
  subFamily?: string;
  isLoading?: boolean;
  isExportableToNifi?: boolean;
  isUploadable?: boolean;
  cliRegIds?: number[];
  parentType?: string;
  transformation?: string;
  isWorkflowMassLaunchable?: boolean;
}

export interface ReportArguments {
  id?: number;
  argumentName: string;
  type: string;
  transId: string;
  displayOrder?: number;
  nbColumns?: number;
  nbRows?: number;
  required: boolean;
  hidden: boolean;
  isRequired?: boolean;
  isHidden?: boolean;
  defaultValue?: string;
  metadatas?: any;
  validation?: string;
  regExp?: string;
  readOnly: boolean;
  isReadOnly?: boolean;
}

export interface ReportFormData {
  descriptor: GroupZoneDescriptor;
  zonesValues: Array<ZoneValues>;
  reportDefinition: ReportDefinition;
}

export interface ReportsFormData {
  descriptors: Map<string | number, GroupZoneDescriptor>;
  zonesValues: Map<string | number, Array<ZoneValues>>;
  data: ReportDefinition[];
}

export enum ReportType {
  CLOSING = 'CLOSING',
  FREEZE = 'FREEZE',
  CERTIFICATE = 'CERTIFICATE',
  PAY_FILE = 'PAY_FILE',
  EMPLOYEE_COMMUNICATION = 'EMPLOYEE_COMMUNICATION',
  EMPLOYEE_ASK_ZONE = 'EMPLOYEE_ASK_ZONE',
  OTHER = 'OTHER',
  MSS = 'MSS',
  SUB_REPORT = 'SUB_REPORT'
}

export interface CliUserPreferences {
  eTemplateAutoSave?: boolean;
  massDataUpdateFavorite?: { cliRegId?: number; reportId?: number };
  massVariablesFavorite?: { cliRegId?: number; reportId?: number };
  generateDocumentsFavorite?: { reportId?: number };
  onboardingFavorite?: { reportId?: number };
}
